import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lock, KeyRound, Eye, Unplug, Plug, Check, X, ServerCog, Database, ScanEye, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { WaitlistCTA } from '../components/WaitlistCTA';
/* ── Data ─────────────────────────────────────────────────────────── */
const CONTROL_PRINCIPLES = [{
  n: '01',
  icon: Eye,
  title: 'You approve every action',
  body: 'Giga never moves money, sends an external email, or pushes code without your explicit sign-off in Slack.'
}, {
  n: '02',
  icon: Unplug,
  title: 'Disconnect at any time',
  body: 'Pause a user, kill a running task, or revoke any integration in one click — no support ticket required.'
}, {
  n: '03',
  icon: Plug,
  title: 'Connect only what you need',
  body: 'Admins decide which tools Giga can reach, who can use them, and at what scope.'
}];
type CertStatus = 'Certified' | 'Aligned' | 'Compliant' | 'Listed' | 'In progress';
const STATUS_STYLES: Record<CertStatus, string> = {
  Certified: 'bg-[#EA580C]/15 text-[#F97316] ring-[#EA580C]/30',
  Aligned: 'bg-white/10 text-neutral-200 ring-white/20',
  Compliant: 'bg-white/10 text-neutral-200 ring-white/20',
  Listed: 'bg-white/10 text-neutral-200 ring-white/20',
  'In progress': 'bg-white/[0.04] text-neutral-500 ring-white/10'
};
const COMPLIANCE: {
  standard: string;
  badge: string;
  status: CertStatus;
  coverage: string;
  documentation: string;
}[] = [{
  standard: 'SOC 2 Type 1',
  badge: 'SOC',
  status: 'Certified',
  coverage: 'Independent attestation that our security controls operate as designed. Type II in progress.',
  documentation: 'Report available under NDA.'
}, {
  standard: 'GDPR',
  badge: 'GDPR',
  status: 'Aligned',
  coverage: 'EU data-protection requirements met across processing and storage.',
  documentation: 'DPA available on request.'
}, {
  standard: 'CCPA',
  badge: 'CCPA',
  status: 'Compliant',
  coverage: 'California Consumer Privacy Act requirements met.',
  documentation: 'Privacy documentation available.'
}, {
  standard: 'CASA Tier 2',
  badge: 'CASA',
  status: 'Certified',
  coverage: 'Cloud Application Security Assessment — required tier for sensitive Google API access.',
  documentation: 'Attestation included in compliance pack.'
}, {
  standard: 'Slack App Directory',
  badge: 'SLACK',
  status: 'Listed',
  coverage: 'OAuth scopes and security posture vetted by Slack before shipment through the store.',
  documentation: 'Public App Directory listing.'
}, {
  standard: 'ISO 27001',
  badge: 'ISO',
  status: 'In progress',
  coverage: 'ISMS controls implementation and evidence collection underway.',
  documentation: 'Controls overview today; evidence after certification.'
}];
const DOES = [{
  title: 'Encrypts everything',
  body: 'TLS 1.2+ in transit. AES-256 at rest. Secrets held in dedicated, access-logged vaults.'
}, {
  title: 'Authenticates with SSO',
  body: 'SAML SSO across Okta, Entra ID, Google Workspace, OneLogin, and any SAML 2.0 IdP.'
}, {
  title: 'Offers data residency',
  body: 'US-hosted by default. EU data residency available on Enterprise contracts.'
}, {
  title: 'Revokes instantly',
  body: 'Admins can disconnect any integration, pause any user, or kill a running task in one click.'
}];
const DOES_NOT = [{
  title: 'Train on your data',
  body: 'Your conversations and files never enter a training set — not ours, not our model providers’.'
}, {
  title: 'Read your secrets',
  body: 'API keys and tokens are injected at execution time by the tool gateway. The model never sees them.'
}, {
  title: 'Act without approval',
  body: 'Money moves, code pushes, and customer emails wait for your explicit approval in Slack.'
}, {
  title: 'Share across workspaces',
  body: 'Skills, integrations, and memory are walled off per workspace. No cross-tenant access.'
}];
const RISK_CONTROLS = [{
  icon: ShieldCheck,
  title: 'Prompt-injection defense',
  body: 'Untrusted content is rendered as data, not commands. High-risk tools sit behind human approval — an injection still can’t move money or push code.'
}, {
  icon: ServerCog,
  title: 'Named providers, no-training contracts',
  body: 'Inference runs on OpenAI, Anthropic, and Google. Each is on our public sub-processor list with a no-training agreement for Giga traffic.'
}, {
  icon: Database,
  title: 'Workspace isolation',
  body: 'Every workspace runs in a sandboxed execution environment. Skills, integrations, and memory are scoped to one tenant — what happens in your Slack stays in your Slack.'
}];
const DATA_PRINCIPLES = [{
  n: '01',
  title: 'Store only what we need',
  body: 'We keep the logs and session data required to make Giga work well — and nothing more. No silent data hoarding.'
}, {
  n: '02',
  title: 'We don’t train a model',
  body: 'Some agent companies quietly use your data to train. We don’t. We provide a service and ask you to pay for it.'
}, {
  n: '03',
  title: 'Your data is encapsulated',
  body: 'Your data and another customer’s data can never touch. Tenants are isolated, and your keys are stored securely, never exposed to the model.'
}];
/* ── Page ─────────────────────────────────────────────────────────── */
export function Security() {
  return <div className="w-full bg-[#FAF8F4] text-[#2B211A] font-body" data-id="element-4812">
      {/* ===== HERO (dark) ===== */}
      <section className="relative w-full bg-[#1C1510] overflow-hidden pt-32 md:pt-40 pb-16 md:pb-24" data-id="element-4813">
        <div className="absolute inset-0 dot-matrix-dark opacity-70 pointer-events-none" data-id="element-4814" />
        <motion.div aria-hidden="true" className="pointer-events-none absolute -top-40 right-0 w-[640px] h-[640px] rounded-full bg-[#F97316]/[0.14] blur-[150px]" animate={{
        x: [0, 30, 0],
        y: [0, 24, 0],
        opacity: [0.6, 1, 0.6]
      }} transition={{
        duration: 16,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-4815" />

        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4816">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center" data-id="element-4817">
            {/* Copy */}
            <div data-id="element-4818">
              <motion.span initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} className="font-mono text-xs tracking-widest uppercase text-neutral-400 mb-5 block" data-id="element-4819">
                Security &amp; compliance — built in from day one
              </motion.span>
              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.1
            }} className="font-title font-bold text-4xl md:text-5xl lg:text-6xl text-white tracking-tight leading-[1.05] mb-6" data-id="element-4820">
                Security you can hand to{' '}
                <span className="text-[#F97316]" data-id="element-4821">your compliance team.</span>
              </motion.h1>
              <motion.p initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="text-base md:text-lg text-neutral-400 leading-relaxed max-w-lg mb-8" data-id="element-4822">
                Your credentials never touch the model. Every sensitive action
                waits for your approval. And your data never trains a model —
                not ours, not anyone’s.
              </motion.p>

              {/* Badge row */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.3
            }} className="flex flex-wrap gap-2.5" data-id="element-4823">
                {['SOC 2', 'GDPR', 'CCPA', 'CASA Tier 2'].map(b => <span key={b} className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full bg-white/[0.04] ring-1 ring-white/10 text-sm font-medium text-neutral-200" data-id="element-4824">
                    <ShieldCheck size={14} className="text-[#F97316]" data-id="element-4825" />
                    {b}
                  </span>)}
              </motion.div>
            </div>

            {/* Tactile editorial panel */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.96
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.7,
            delay: 0.2
          }} className="relative hidden lg:block" data-id="element-4826">
              <div className="relative aspect-[4/3] rounded-3xl bg-[#241A12] ring-1 ring-white/10 overflow-hidden" data-id="element-4827">
                <div className="absolute inset-0 dot-matrix-dark opacity-50" data-id="element-4828" />
                {/* binary murmur */}
                <div className="absolute inset-0 p-6 font-mono text-[10px] leading-relaxed text-white/[0.06] select-none overflow-hidden" data-id="element-4829">
                  {Array.from({
                  length: 22
                }).map((_, i) => <div key={i} className="whitespace-nowrap" data-id="element-4830">
                      01010011 01100101 01100011 01110101 01110010 01100101
                      00100000 01100010 01111001 00100000 01100100 01100101
                    </div>)}
                </div>
                {/* central lock motif */}
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-4831">
                  <div className="relative" data-id="element-4832">
                    <div className="absolute inset-0 -m-8 rounded-full bg-[#F97316]/10 blur-2xl" data-id="element-4833" />
                    <div className="relative w-28 h-28 rounded-3xl bg-[#1C1510] ring-1 ring-white/10 flex items-center justify-center shadow-2xl shadow-black/40" data-id="element-4834">
                      <Lock size={44} className="text-[#F97316]" strokeWidth={1.75} data-id="element-4835" />
                    </div>
                  </div>
                </div>
                {/* vintage stamp accent */}
                <div className="absolute bottom-5 left-5 w-16 h-20 rounded-sm bg-[#EA580C] rotate-[-8deg] ring-4 ring-[#F5E9DC] shadow-lg flex items-center justify-center" data-id="element-4836">
                  <KeyRound size={26} className="text-[#1C1510]" data-id="element-4837" />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== YOU'RE IN CONTROL (cream) ===== */}
      <section className="w-full bg-[#FAF8F4] py-16 md:py-28 border-b border-neutral-200" data-id="element-4838">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4839">
          <motion.h2 initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} className="font-title font-bold text-3xl md:text-5xl tracking-tight mb-12 md:mb-16" data-id="element-4840">
            You’re in control.
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-dashed border-neutral-300" data-id="element-4841">
            {CONTROL_PRINCIPLES.map((p, i) => {
            const Icon = p.icon;
            return <motion.div key={p.n} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="py-8 md:py-10 md:px-8 md:first:pl-0 border-b md:border-b-0 md:border-r last:border-r-0 last:border-b-0 border-dashed border-neutral-300" data-id="element-4842">
                  <div className="flex items-center justify-between mb-8 md:mb-12" data-id="element-4843">
                    <span className="font-title italic font-bold text-lg text-[#EA580C]" data-id="element-4844">
                      {p.n}
                    </span>
                    <Icon size={18} className="text-[#EA580C]" data-id="element-4845" />
                  </div>
                  <h3 className="font-title font-bold text-xl md:text-2xl mb-2" data-id="element-4846">
                    {p.title}
                  </h3>
                  <p className="text-neutral-600 leading-relaxed" data-id="element-4847">{p.body}</p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ===== COMPLIANCE TABLE (dark) ===== */}
      <section className="w-full bg-[#1C1510] py-16 md:py-28 relative overflow-hidden" data-id="element-4848">
        <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-4849" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4850">
          <div className="max-w-2xl mb-12 md:mb-16" data-id="element-4851">
            <span className="font-mono text-xs tracking-widest uppercase text-[#F97316] mb-4 block" data-id="element-4852">
              Compliance
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl text-white tracking-tight leading-[1.1]" data-id="element-4853">
              Independently audited. Continuously verified.
            </h2>
            <p className="text-neutral-400 mt-5 leading-relaxed" data-id="element-4854">
              The audit reports are real, the controls are monitored, and the
              next audit is always on the calendar.
            </p>
          </div>

          {/* Column headers (desktop) */}
          <div className="hidden lg:grid grid-cols-[1.2fr_0.8fr_2fr_1.4fr] gap-6 px-6 pb-4 border-b border-white/10 font-mono text-[11px] uppercase tracking-wider text-neutral-500" data-id="element-4855">
            <span data-id="element-4856">Standard</span>
            <span data-id="element-4857">Status</span>
            <span data-id="element-4858">Coverage</span>
            <span data-id="element-4859">Documentation</span>
          </div>

          <div className="flex flex-col" data-id="element-4860">
            {COMPLIANCE.map((c, i) => <motion.div key={c.standard} initial={{
            opacity: 0,
            y: 12
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-60px'
          }} transition={{
            duration: 0.4,
            delay: i * 0.06
          }} className="grid grid-cols-1 lg:grid-cols-[1.2fr_0.8fr_2fr_1.4fr] gap-3 lg:gap-6 px-5 lg:px-6 py-6 rounded-2xl lg:rounded-none border border-white/10 lg:border-0 lg:border-b lg:border-white/10 bg-white/[0.03] lg:bg-transparent mb-3 lg:mb-0 hover:bg-white/[0.04] transition-colors" data-id="element-4861">
                {/* Standard */}
                <div className="flex items-center gap-3" data-id="element-4862">
                  <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 text-[#1C1510] font-mono text-[9px] font-bold leading-none text-center" data-id="element-4863">
                    {c.badge}
                  </div>
                  <span className="text-white font-semibold" data-id="element-4864">{c.standard}</span>
                </div>
                {/* Status */}
                <div className="flex items-center" data-id="element-4865">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ring-1 ${STATUS_STYLES[c.status]}`} data-id="element-4866">
                    {c.status}
                  </span>
                </div>
                {/* Coverage */}
                <p className="text-sm text-neutral-300 leading-relaxed" data-id="element-4867">
                  {c.coverage}
                </p>
                {/* Documentation */}
                <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-4868">
                  {c.documentation}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* ===== DOES / DOES NOT (cream) ===== */}
      <section className="w-full bg-[#FAF8F4] py-16 md:py-28" data-id="element-4869">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4870">
          <div className="text-center max-w-2xl mx-auto mb-12 md:mb-16" data-id="element-4871">
            <span className="font-mono text-xs tracking-widest uppercase text-[#EA580C] mb-4 block" data-id="element-4872">
              Data handling
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight leading-[1.1]" data-id="element-4873">
              What Giga does. What Giga does not.
            </h2>
            <p className="text-neutral-600 mt-5 leading-relaxed" data-id="element-4874">
              Exactly what Giga touches — and what it never does.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-10" data-id="element-4875">
            {/* Does */}
            <div data-id="element-4876">
              <div className="flex items-center gap-2.5 mb-5" data-id="element-4877">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-[#EA580C] text-white" data-id="element-4878">
                  <Check size={16} strokeWidth={3} data-id="element-4879" />
                </span>
                <span className="font-title font-bold text-lg" data-id="element-4880">Does</span>
              </div>
              <div className="flex flex-col gap-3" data-id="element-4881">
                {DOES.map((d, i) => <motion.div key={d.title} initial={{
                opacity: 0,
                y: 12
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.06
              }} className="flex items-start gap-3 p-5 rounded-2xl bg-white border border-neutral-200" data-id="element-4882">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#EA580C]/15 text-[#EA580C] shrink-0" data-id="element-4883">
                      <Check size={13} strokeWidth={3} data-id="element-4884" />
                    </span>
                    <div data-id="element-4885">
                      <h3 className="font-semibold text-[#2B211A] mb-1" data-id="element-4886">
                        {d.title}
                      </h3>
                      <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-4887">
                        {d.body}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>

            {/* Does not */}
            <div data-id="element-4888">
              <div className="flex items-center gap-2.5 mb-5" data-id="element-4889">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-neutral-300 text-neutral-600" data-id="element-4890">
                  <X size={16} strokeWidth={3} data-id="element-4891" />
                </span>
                <span className="font-title font-bold text-lg text-neutral-500" data-id="element-4892">
                  Does not
                </span>
              </div>
              <div className="flex flex-col gap-3" data-id="element-4893">
                {DOES_NOT.map((d, i) => <motion.div key={d.title} initial={{
                opacity: 0,
                y: 12
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                duration: 0.4,
                delay: i * 0.06
              }} className="flex items-start gap-3 p-5 rounded-2xl bg-[#F1ECE3] border border-neutral-200" data-id="element-4894">
                    <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-neutral-300 text-neutral-600 shrink-0" data-id="element-4895">
                      <X size={13} strokeWidth={3} data-id="element-4896" />
                    </span>
                    <div data-id="element-4897">
                      <h3 className="font-semibold text-neutral-700 mb-1" data-id="element-4898">
                        {d.title}
                      </h3>
                      <p className="text-sm text-neutral-500 leading-relaxed" data-id="element-4899">
                        {d.body}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== AI RISK CONTROLS (dark) ===== */}
      <section className="w-full bg-[#1C1510] py-16 md:py-28 relative overflow-hidden" data-id="element-4900">
        <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-4901" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4902">
          <div className="max-w-2xl mb-12 md:mb-16" data-id="element-4903">
            <span className="font-mono text-xs tracking-widest uppercase text-[#F97316] mb-4 block" data-id="element-4904">
              AI safety
            </span>
            <h2 className="font-title font-bold text-3xl md:text-5xl text-white tracking-tight leading-[1.1]" data-id="element-4905">
              AI brings new risks. We built for them.
            </h2>
            <p className="text-neutral-400 mt-5 leading-relaxed" data-id="element-4906">
              An AI coworker introduces attack surfaces traditional SaaS
              doesn’t. Three controls keep that surface small.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8" data-id="element-4907">
            {RISK_CONTROLS.map((r, i) => {
            const Icon = r.icon;
            return <motion.div key={r.title} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="p-7 md:p-8 rounded-3xl bg-white/[0.03] ring-1 ring-white/10" data-id="element-4908">
                  <div className="w-12 h-12 rounded-2xl bg-[#EA580C]/15 ring-1 ring-[#EA580C]/30 flex items-center justify-center mb-6" data-id="element-4909">
                    <Icon size={22} className="text-[#F97316]" data-id="element-4910" />
                  </div>
                  <h3 className="font-title font-bold text-xl text-white mb-3" data-id="element-4911">
                    {r.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed text-sm md:text-base" data-id="element-4912">
                    {r.body}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* ===== DATA & ENGINEERING PRINCIPLES (cream) ===== */}
      <section className="w-full bg-[#FAF8F4] py-16 md:py-28 border-t border-neutral-200" data-id="element-4913">
        <div className="max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4914">
          <div className="grid grid-cols-1 lg:grid-cols-[0.9fr_1.1fr] gap-12 lg:gap-20" data-id="element-4915">
            {/* Heading + tactile motif */}
            <div className="lg:sticky lg:top-32 self-start" data-id="element-4916">
              <h2 className="font-title font-bold text-3xl md:text-5xl tracking-tight leading-[1.05] mb-8" data-id="element-4917">
                Giga’s data &amp; engineering principles.
              </h2>
              <div className="relative w-full max-w-xs aspect-square" data-id="element-4918">
                <div aria-hidden="true" className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] aspect-square rounded-full opacity-60" style={{
                backgroundImage: 'radial-gradient(circle, #d6d3d1 1.25px, transparent 1.25px)',
                backgroundSize: '14px 14px',
                WebkitMaskImage: 'radial-gradient(circle, black 35%, transparent 70%)',
                maskImage: 'radial-gradient(circle, black 35%, transparent 70%)'
              }} data-id="element-4919" />
                <div className="absolute inset-0 flex items-center justify-center" data-id="element-4920">
                  <div className="w-28 h-28 rounded-3xl bg-white ring-1 ring-neutral-200 shadow-xl shadow-orange-900/5 flex items-center justify-center" data-id="element-4921">
                    <ScanEye size={42} className="text-[#EA580C]" strokeWidth={1.75} data-id="element-4922" />
                  </div>
                </div>
              </div>
            </div>

            {/* Numbered list */}
            <div className="flex flex-col" data-id="element-4923">
              {DATA_PRINCIPLES.map((p, i) => <motion.div key={p.n} initial={{
              opacity: 0,
              y: 16
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="flex gap-5 md:gap-8 py-8 border-b border-dashed border-neutral-300 first:pt-0 last:border-b-0" data-id="element-4924">
                  <span className="font-title italic font-bold text-xl text-[#EA580C] shrink-0 pt-1" data-id="element-4925">
                    {p.n}
                  </span>
                  <div data-id="element-4926">
                    <h3 className="font-title font-bold text-xl md:text-2xl mb-3" data-id="element-4927">
                      {p.title}
                    </h3>
                    <p className="text-neutral-600 leading-relaxed" data-id="element-4928">{p.body}</p>
                  </div>
                </motion.div>)}
            </div>
          </div>
        </div>
      </section>

      {/* ===== CLOSING CTA (dark) ===== */}
      <section className="w-full bg-[#1C1510] py-16 md:py-24 relative overflow-hidden" data-id="element-4929">
        <div className="absolute inset-0 dot-matrix-dark opacity-50 pointer-events-none" data-id="element-4930" />
        <div className="relative z-10 max-w-[1280px] mx-auto px-6 md:px-10" data-id="element-4931">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 rounded-3xl bg-white/[0.03] ring-1 ring-white/10 p-8 md:p-12" data-id="element-4932">
            <div className="max-w-xl" data-id="element-4933">
              <h2 className="font-title font-bold text-2xl md:text-4xl text-white tracking-tight mb-3 leading-tight" data-id="element-4934">
                Questions for your security review?
              </h2>
              <p className="text-neutral-400 leading-relaxed" data-id="element-4935">
                Request our compliance pack, a DPA, or a deep-dive with our
                team. We’ll get your procurement unblocked fast.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0" data-id="element-4936">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[#EA580C] hover:bg-[#C2410C] text-white px-7 py-3.5 rounded-full font-bold transition-colors" data-id="element-4937">
                Talk to our team
                <ArrowRight size={16} data-id="element-4938" />
              </Link>
              <WaitlistCTA className="inline-flex items-center justify-center bg-white/[0.06] hover:bg-white/10 ring-1 ring-white/10 text-white px-7 py-3.5 rounded-full font-medium transition-colors" data-id="element-4939">
                Try Giga for free
              </WaitlistCTA>
            </div>
          </div>
        </div>
      </section>
    </div>;
}